import React, { useState } from 'react';

// Components
import { FileInfo } from '@uploadcare/react-widget';
import MultiPhotoCta from 'components/common/MultiPhotoUpload/MultiPhotoCta';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';

// Helpers
import openUploadcareModal from '@zola-helpers/client/dist/es/uploadcare/openUploadcareModal';

// Reducers
import { useAppDispatch } from 'reducers/index';

// Actions
import { saveUploadcareImagesToZola } from 'actions/UploadcareActions';
import { ZolaFileResponseType } from 'actions/types/UploadcareActions';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';

// Types
import { UploadCareTabTypes } from '@zola-helpers/client/dist/es/uploadcare/constants';
import type { UploadcareModalProps } from '@zola-helpers/client/dist/es/uploadcare/openUploadcareModal';

// Styles
import './MultiPhotoUpload.module.less';

type MultiPhotoUploadProps = Omit<UploadcareModalProps, 'onUploadcareFileReady'> & {
  onUploadComplete: (zolaFiles: ZolaFileResponseType[]) => void;
  btnView?: boolean;
  btnOverrideTxt?: string;
  ctaOverrideTxt?: {
    titleTxt?: string;
    labelTxt?: string;
    btnTxt?: string;
  };
  isDisabled?: boolean;
};

const MultiPhotoUpload = ({
  btnView,
  btnOverrideTxt,
  ctaOverrideTxt,
  cropRatio = '1:1',
  dynamicPreviewTitle = true,
  effects = 'blur,crop,enhance,grayscale,mirror,sharp',
  maxFileCount = 100,
  maxSize = 20,
  onUploadComplete,
  styleId = 'MultiPhotoUpload',
  tabs = [
    UploadCareTabTypes.FILE,
    UploadCareTabTypes.DROPBOX,
    UploadCareTabTypes.GOOGLE_PHOTOS,
    UploadCareTabTypes.ONE_DRIVE,
    UploadCareTabTypes.FACEBOOK,
    UploadCareTabTypes.INSTAGRAM,
  ],
  isDisabled,
  ...UploadcareModalOptions
}: MultiPhotoUploadProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const openUploadcare = () => {
    setIsBusy(false);
    const handleOnFileReady = (uploadcareFiles: FileInfo[]) => {
      setIsBusy(true);
      dispatch(saveUploadcareImagesToZola(uploadcareFiles))
        .then((zolaFiles: ZolaFileResponseType[]) => {
          onUploadComplete(zolaFiles);
        })
        .catch(() => {
          dispatch(
            toastsActions.negative({ headline: 'There was an error processing your photos' })
          );
        })
        .finally(() => {
          setIsBusy(false);
        });
    };
    openUploadcareModal({
      cropRatio,
      dynamicPreviewTitle,
      effects,
      maxFileCount,
      maxSize,
      styleId,
      tabs,
      ...UploadcareModalOptions,
      onUploadcareFileReady: (_fileGroupId, uploadcareFiles) => {
        if (uploadcareFiles && uploadcareFiles.length > 0) {
          handleOnFileReady(uploadcareFiles);
        }
      },
    });
  };

  if (btnView) {
    return (
      <ButtonV3
        type="button"
        variant="primary"
        onClick={openUploadcare}
        disabled={isBusy || isDisabled}
      >
        {btnOverrideTxt || 'Add More Photos'}
      </ButtonV3>
    );
  }
  return (
    <MultiPhotoCta
      handleToggleMultiPhotoModal={openUploadcare}
      isDisabled={isBusy || isDisabled}
      ctaOverrideTxt={ctaOverrideTxt}
    />
  );
};

export default MultiPhotoUpload;
